import React, { useEffect, useState } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { FaPercentage } from 'react-icons/fa';
import { toast } from '../../toasts';
import commissionService from '../../services/commission.service';
import ConfigService from '../../services/settings.service';
import LoadingTableBox from '../../components/LoadingTableBox';
import MessageBox from '../../components/MessageBox';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import CommissionEditor from './CommissionEditor';
import { getError } from '../../utils';
import { Section, StatStrip, DataTable, Pill, RowActionsMenu, Button, FormField, controlClass } from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

/**
 * Displays commissions for a given contract or assetSale.
 * Props:
 *   contractId: string | null
 *   assetSaleId: string | null
 *   parentAmount: number | null  — used to auto-calculate commission amount
 *   parentCurrency: string | null
 */
const CommissionList = ({ contractId, assetSaleId, parentAmount, parentCurrency }) => {
	const { t } = useLabels();
	const [commissions, setCommissions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [showEditor, setShowEditor] = useState(false);
	const [editing, setEditing] = useState(null);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [creatingInmobiliaria, setCreatingInmobiliaria] = useState(false);
	const [showInmobiliariaModal, setShowInmobiliariaModal] = useState(false);
	const [inmobiliariaPercentage, setInmobiliariaPercentage] = useState('');
	const [inmobiliariaError, setInmobiliariaError] = useState('');
	const [inmobiliariaFieldError, setInmobiliariaFieldError] = useState('');

	const inmobiliariaTitle = contractId ? 'Agregar comisión alquiler' : 'Agregar comisión ventas';

	const fetchCommissions = async () => {
		setLoading(true);
		try {
			const filters = {};
			if (contractId) filters.contract = contractId;
			if (assetSaleId) filters.assetSale = assetSaleId;
			const res = await commissionService.list(filters);
			setCommissions(res.data || []);
		} catch (err) {
			setError(getError(err));
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchCommissions();
	}, [contractId, assetSaleId]);

	const handleDelete = async () => {
		try {
			await commissionService.delete(deleteTarget._id);
			toast.success('Comisión eliminada');
			setDeleteTarget(null);
			fetchCommissions();
		} catch (err) {
			toast.error(getError(err));
		}
	};

	const handleOpenInmobiliariaModal = async () => {
		setInmobiliariaPercentage('');
		setInmobiliariaError('');
		setInmobiliariaFieldError('');
		setShowInmobiliariaModal(true);
		try {
			const res = await ConfigService.listPublic();
			const settings = res.data || {};
			const defaultPct = contractId ? settings.commissionPercentageRental : settings.commissionPercentageSales;
			if (defaultPct != null) {
				setInmobiliariaPercentage(String(defaultPct));
			}
		} catch (err) {
			// Silently ignore — user can still enter a percentage manually
		}
	};

	const handleInmobiliaria = async () => {
		if (!inmobiliariaPercentage) {
			const message = 'Complete el/los siguiente/s campo/s requerido/s: Porcentaje (%).';
			setInmobiliariaFieldError('Este campo es obligatorio.');
			setInmobiliariaError(message);
			toast.error(message);
			return;
		}
		setInmobiliariaFieldError('');
		setInmobiliariaError('');

		try {
			setCreatingInmobiliaria(true);
			await commissionService.createInmobiliaria({
				contract: contractId || undefined,
				assetSale: assetSaleId || undefined,
				parentAmount,
				parentCurrency,
				percentage: inmobiliariaPercentage ? parseFloat(inmobiliariaPercentage) : undefined,
			});
			toast.success('Comisión inmobiliaria registrada');
			setShowInmobiliariaModal(false);
			fetchCommissions();
		} catch (err) {
			setInmobiliariaError(getError(err));
			toast.error(getError(err));
		} finally {
			setCreatingInmobiliaria(false);
		}
	};

	const getSellerName = (commission) => {
		if (commission.isExternalSeller && commission.externalSellerName) {
			return `${commission.externalSellerName} (externo)`;
		}
		return commission.seller?.name || '—';
	};

	if (loading) return <LoadingTableBox />;
	if (error) return <MessageBox variant="danger">{error}</MessageBox>;

	const stats = [
		{ label: 'Comisiones', value: commissions.length },
	];

	const columns = [
		{ key: 'seller', header: 'Vendedor', render: (c) => getSellerName(c) },
		{
			key: 'amount',
			header: 'Monto',
			className: 'tw:text-right',
			render: (c) => <span className='tw:font-mono tw:tabular-nums'>{c.amount}</span>,
		},
		{ key: 'currency', header: 'Moneda', className: 'tw:font-mono', render: (c) => c.currency },
		{
			key: 'percentage',
			header: '%',
			className: 'tw:text-right tw:font-mono',
			render: (c) => (c.percentage != null ? `${c.percentage}%` : '—'),
		},
		{
			key: 'calculationBase',
			header: 'Base',
			render: (c) => <Pill tone='accent'>{c.calculationBase === 'compound' ? 'Compuesta' : 'Base'}</Pill>,
		},
		{ key: 'observations', header: 'Observaciones', render: (c) => c.observations || '—' },
	];

	const renderRowActions = (c) => (
		<RowActionsMenu
			items={[
				{ label: 'Editar', onClick: () => { setEditing(c); setShowEditor(true); } },
				{ label: 'Eliminar', onClick: () => setDeleteTarget(c), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end tw:gap-2'>
				<Button size='sm' onClick={handleOpenInmobiliariaModal} disabled={creatingInmobiliaria}>
					{inmobiliariaTitle}
				</Button>
				<Button size='sm' variant='primary' onClick={() => { setEditing(null); setShowEditor(true); }}>
					Personalizado
				</Button>
			</div>
			<Section title='Comisiones'>
				<DataTable
					columns={columns}
					rows={commissions}
					loading={false}
					error=''
					rowActions={renderRowActions}
					emptyMessage='No hay comisiones registradas.'
				/>
			</Section>

			{showEditor && (
				<CommissionEditor
					show={showEditor}
					onHide={() => setShowEditor(false)}
					onSaved={() => { setShowEditor(false); fetchCommissions(); }}
					initialData={editing}
					contractId={contractId}
					assetSaleId={assetSaleId}
					parentAmount={parentAmount}
					parentCurrency={parentCurrency}
				/>
			)}

			<ConfirmationModal
				show={!!deleteTarget}
				handleClose={() => setDeleteTarget(null)}
				handleConfirm={handleDelete}
				title={t('title.deleteCommission')}
				description={t('msg.confirmDeleteCommission')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>

			<Modal
				show={showInmobiliariaModal}
				onHide={() => setShowInmobiliariaModal(false)}
				animation={false}
				centered
				backdrop={creatingInmobiliaria ? 'static' : true}
				keyboard={!creatingInmobiliaria}
				dialogClassName='tw:max-w-[460px]'
				contentClassName='entity-list-page el-modal tw:rounded-[14px] tw:border-0 tw:overflow-hidden tw:shadow-overlay tw:font-sans tw:text-[14px]/[1.45] tw:text-ink-0'
			>
				<Modal.Header
					closeButton={!creatingInmobiliaria}
					className='tw:items-start tw:border-b tw:border-border tw:px-[22px] tw:pt-[20px] tw:pb-[14px]'
				>
					<div className='tw:flex tw:items-start tw:gap-3'>
						<span className='tw:mt-0.5 tw:flex tw:h-[38px] tw:w-[38px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[9px] tw:bg-accent-soft tw:text-accent'>
							<FaPercentage />
						</span>
						<div className='tw:min-w-0'>
							<Modal.Title className='tw:text-[17px]/[1.3] tw:font-bold tw:tracking-[-0.01em] tw:text-ink-0'>
								{inmobiliariaTitle}
							</Modal.Title>
							<p className='tw:mb-0 tw:mt-1 tw:text-[12.5px] tw:text-ink-2'>
								Se propone el porcentaje configurado; podés ajustarlo antes de confirmar.
							</p>
						</div>
					</div>
				</Modal.Header>
				<Modal.Body className='tw:flex tw:flex-col tw:gap-3.5 tw:px-[22px] tw:py-[18px]'>
					{inmobiliariaError && (
						<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
							{inmobiliariaError}
						</div>
					)}
					<FormField id='inmobiliaria-percentage' label='Porcentaje (%)' required>
						<Form.Control
							id='inmobiliaria-percentage'
							type='number'
							min='0'
							max='100'
							step='0.01'
							className={controlClass}
							value={inmobiliariaPercentage}
							onChange={(e) => { setInmobiliariaPercentage(e.target.value); setInmobiliariaFieldError(''); }}
							placeholder='0.00'
							disabled={creatingInmobiliaria}
						/>
						<FieldError message={inmobiliariaFieldError} />
					</FormField>
				</Modal.Body>
				<Modal.Footer className='tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px] tw:gap-2'>
					<Button onClick={() => setShowInmobiliariaModal(false)} disabled={creatingInmobiliaria}>
						Cancelar
					</Button>
					<Button variant='primary' onClick={handleInmobiliaria} disabled={creatingInmobiliaria}>
						{creatingInmobiliaria ? 'Creando...' : 'Confirmar'}
					</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default CommissionList;
